import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Optional } from '@angular/core';

import type { AxonUsherAlertModel, AxonNotificationIconService } from '@lib/axon';

import { SnMapClusterPoint } from '@lib/api';
import { CRITICAL_EVENT_TYPES } from '@lib/common/helpers';
import { FsFormatDatePipe } from '@lib/common/pipes';
import { SnLonLat, SnLonLatBounds } from '@lib/geo/helpers';
import { SnBadgeConfig } from '@lib/ui/badge';

import { GoogleMap } from '../../map';
import { GoogleMapPopup } from '../../popup';
import { IAdvancedBadgeOptions } from './advanced-feature-marker';
import { IGoogleMap } from './interfaces/google-map.interface';
import { IGoogleMarker } from './interfaces/google-marker.interface';
import {
  GoogleMapMarkerIcon,
  IGoogleMapMarkerIconLabel,
  IGoogleMapMarkerLabel,
  IGoogleMapMarkerStyleOptions,
} from './marker-options';

const DEFAULT_OPTIONS: google.maps.marker.AdvancedMarkerElementOptions = {
  title: '',
};

interface IBannerContent {
  data: AxonUsherAlertModel;
  icon: string;
}

export class FsAdvancedMarker implements IGoogleMarker {
  get latLngChange$(): Observable<google.maps.LatLng> {
    return this._latLngChange$.asObservable();
  }

  get removed$(): Observable<void> {
    return this._removed$.asObservable();
  }

  get map(): GoogleMap {
    return this._map;
  }

  public marker: google.maps.marker.AdvancedMarkerElement;
  public animationOn: boolean = false;
  public draggable: boolean = false;
  public badge: any;
  public badgeWithCustomText: HTMLSpanElement | undefined = undefined;
  public banner: HTMLDivElement;
  public bannerContent: IBannerContent[] = [];
  public deviceHistory: any[] = [];
  public heading: number;
  public statusBadge: HTMLElement;

  // Additional properties required by IGoogleMarker interface
  public feature?: google.maps.Data.Feature | unknown;
  public iconLabel?: IGoogleMapMarkerIconLabel;
  public address?: string;

  // Private properties for IGoogleMarker interface
  private _zIndex: number = 0;
  private _icon?: GoogleMapMarkerIcon;
  private _style?: IGoogleMapMarkerStyleOptions;
  private _badge?: SnBadgeConfig;
  private _label?: IGoogleMapMarkerLabel;
  private _rotation: number = 0;

  private _popup: GoogleMapPopup;
  private _map: GoogleMap;
  private readonly _latLngChange$ = new Subject<google.maps.LatLng>();
  private readonly _removed$ = new Subject<void>();
  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Optional() options?: google.maps.marker.AdvancedMarkerElementOptions,
    @Optional() animationClass?: string,
    @Optional() public cluster?: SnMapClusterPoint,
    @Optional() private _axonNotificationIconService?: AxonNotificationIconService,
  ) {
    options = {
      content: new google.maps.marker.PinElement().element,
      collisionBehavior: google.maps.CollisionBehavior.REQUIRED,
      ...DEFAULT_OPTIONS,
      ...options,
    };
    this.marker = new google.maps.marker.AdvancedMarkerElement(options);
    this.marker.element.classList.add('fs__advanced-marker');
    this.animationOn = !!animationClass;
    if (this.animationOn) {
      this._initAnimation(this.marker, animationClass);
    }
    if (cluster) {
      this.onClusterClick();
    }
    this._latLngChange$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this._updatePosition();
    });

    // Initialize IGoogleMarker properties
    this._zIndex = options?.zIndex ?? 0;
  }

  public getMarker(): google.maps.marker.AdvancedMarkerElement {
    return this.marker;
  }

  public getTitle(): string {
    return this.getMarker().title;
  }

  public setTitle(value: string): void {
    this.getMarker().title = value;
  }

  public getLonLat(): SnLonLat {
    const position: any = this.getMarker().position;
    return new SnLonLat(position.lng, position.lat);
  }

  public setLonLat(lonLat: SnLonLat): void {
    const latLng = new google.maps.LatLng(lonLat.lat, lonLat.lon);
    this.getMarker().position = latLng;
    this._latLngChange$.next(latLng);
  }

  public getPosition(): any {
    return this.getMarker().position;
  }

  public getIconElement(isEmpty: boolean): any {
    if (isEmpty) {
      return null;
    }
  }

  public setOptions(options: any): void {
    this.draggable = options.draggable;
  }

  public setDraggable(draggable: boolean): void {
    this.draggable = draggable;
    this.marker.gmpDraggable = this.draggable;
  }

  public getDraggable(): boolean {
    return this.draggable;
  }

  public getPopup(): GoogleMapPopup {
    return this._popup;
  }

  public addTo(map: GoogleMap | IGoogleMap): void {
    this._map = map as GoogleMap;
    this.marker.map = this._map.mapInstance;
  }

  public remove(keepPopup?: boolean): void {
    this.marker.map = null;
    if (this._popup && !keepPopup) {
      this._popup.close();
    }
    this._removed$.next();
    this._destroy$.next();
  }

  public setPopup(popup: GoogleMapPopup): void {
    if (this._popup) {
      this._popup.close();
    }
    this._popup = popup;
    this._popup.open(this._map);
  }

  public setRotation(rotation: number): void {
    this._rotation = rotation;
    const content: any = this.getMarker().content;
    const img = content.querySelector('.fs__camera-advanced-icon');
    img
      ? (img.style.transform = `rotate(${rotation - 90}deg)`)
      : (content.style.transform = `rotate(${rotation}deg)`);
  }

  // IGoogleMarker interface methods

  public getZIndex(): number {
    return this._zIndex;
  }

  public setZIndex(zIndex: number): void {
    this._zIndex = zIndex;
    this.marker.zIndex = zIndex;
  }

  public getIcon(): GoogleMapMarkerIcon | undefined {
    return this._icon;
  }

  public setIcon(icon: GoogleMapMarkerIcon): void {
    this._icon = icon;
    // Implementation would depend on the specific icon type
    // This is a placeholder - actual implementation would be more complex
  }

  public getStyle(): IGoogleMapMarkerStyleOptions | undefined {
    return this._style;
  }

  public setStyle(style: IGoogleMapMarkerStyleOptions): void {
    this._style = style;
    // Implementation would apply the style to the marker
    // This is a placeholder - actual implementation would be more complex
  }

  public getBadge(): SnBadgeConfig | undefined {
    return this._badge;
  }

  public setBadge(badge: SnBadgeConfig): void {
    this._badge = badge;
    // Implementation would create/update the badge
    // This is a placeholder - actual implementation would be more complex
  }

  public getLabel(): IGoogleMapMarkerLabel | undefined {
    return this._label;
  }

  public setLabel(label: string | IGoogleMapMarkerLabel): void {
    if (typeof label === 'string') {
      this._label = { text: label };
    } else {
      this._label = label;
    }
    // Implementation would create/update the label
    // This is a placeholder - actual implementation would be more complex
  }

  public getRotation(): number {
    return this._rotation;
  }

  public createBadge(): void {
    const badge = document.createElement('span');
    badge.innerText = this.cluster.properties.objects?.toString();
    badge.classList.add('fs__advanced-marker-cluster-badge');
    this.getMarker().content.appendChild(badge);
  }

  public createBadgeWithCustomText(
    content: string,
    badgePosition: IAdvancedBadgeOptions = {},
  ): void {
    if (!this.badgeWithCustomText) {
      this.badgeWithCustomText = document.createElement('span');
      this.getMarker().content.appendChild(this.badgeWithCustomText);
    }
    this.badgeWithCustomText.innerText = content;
    this.badgeWithCustomText.classList.add('fs__advanced-marker-cluster-badge');
    badgePosition = { bottom: '16px', right: '-9px', ...badgePosition }; // This positions the badge to the top-0right of the marker.
    Object.entries(badgePosition).forEach(
      ([position, offset]) => (this.badgeWithCustomText.style[position] = offset),
    );
  }

  public removeBadgeWithCustomText(): void {
    if (this.badgeWithCustomText) {
      try {
        this.getMarker().content.removeChild(this.badgeWithCustomText);
      } catch (e) {
        /** No action needed. This catch block prevents console errors when attempting to remove a non-existent badge. */
      } finally {
        this.badgeWithCustomText = undefined;
      }
    }
  }

  public setBadgeStatus(status: string, badgePosition: IAdvancedBadgeOptions): void {
    if (this.statusBadge) {
      this.statusBadge.style.backgroundColor = status;
      Object.entries(badgePosition).forEach(([position, offset]) => {
        this.statusBadge.style[position] = offset;
      });
    } else {
      const badge = document.createElement('span');
      badge.classList.add('fs__advanced-marker-status-badge');
      badge.style.backgroundColor = status;
      Object.entries(badgePosition).forEach(([position, offset]) => {
        badge.style[position] = offset;
      });
      this.getMarker().content.appendChild(badge);
      this.statusBadge = badge;
    }
  }

  public createSelectedBadge(): void {
    this.badge = document.createElement('span');
    this.badge.innerText = '✔';
    this.badge.classList.add('fs__advanced-marker-selected-badge');
    this.badge.id = 'selected-badge';
    this.getMarker().content.appendChild(this.badge);
  }

  public createIconBadge(iconCodeSnippet: string): void {
    const badge = document.createElement('span');
    badge.innerHTML = iconCodeSnippet;
    badge.classList.add('fs__advanced-marker-icon-badge');
    this.getMarker().content.appendChild(badge);
  }

  public removeSelectedBadge(): void {
    if (this.badge) {
      this.getMarker().content.removeChild(this.badge);
    }
  }

  public addDeviceHistory(data: any): void {
    this.deviceHistory.push(data);
  }

  public createBanner(): void {
    if (!this.banner) {
      this.banner = document.createElement('div');
    }
    const gmMarker: any = this.getMarker();
    gmMarker.axonNotifications = this.bannerContent.map((content) => content.data);
  }

  public createNotificationBanner(data: AxonUsherAlertModel): void {
    if (!this.banner) {
      this.banner = document.createElement('div');
    }

    // Check if this notification already exists in bannerContent to prevent duplicates
    const existingNotification = this.bannerContent.find(
      (content) => content.data.notification.id === data.notification.id,
    );
    if (existingNotification) {
      return;
    }

    const icon = this._generateIcon(data.notification.category);
    this.banner.onmouseenter = () => {
      this._onBannerHover(this.banner);
    };
    this.bannerContent.push({
      data,
      icon: `<i class="fa-solid ${icon} fs-notification-banner-icon"></i>`,
    });
    this.deviceHistory.push({
      attributes: {
        category: data.notification.category,
        device: {
          deviceId: data.device.deviceId,
        },
        timestamp: data.notification.timeOccurred,
      },
    });
    const uniqueIcons = [...new Set(this.bannerContent.map((content) => content.icon))];
    this.banner.innerHTML = uniqueIcons.join(' ');
    this.banner.classList.add('fs__advanced-marker-icon-banner');

    this._updateBannerCriticalState();

    this.getMarker().content.appendChild(this.banner);
    const gmMarker: any = this.getMarker();
    gmMarker.axonNotifications = this.bannerContent.map((content) => content.data);
  }

  public removeNotification(): void {
    if (!this.banner) {
      return;
    }
    const expandedContent = this.getMarker().element.querySelector(
      'fs__advanced-marker-icon-banner-expanded',
    );
    if (expandedContent) {
      expandedContent.remove();
    }
    this.bannerContent = [];
    this.banner.remove();
    this.banner = null;
    const gmMarker: any = this.getMarker();
    gmMarker.axonNotifications = null;
  }

  protected _updateBannerCriticalState(): void {
    if (!this.banner) {
      return;
    }

    const hasCriticalEvent = this.bannerContent.some((content) =>
      CRITICAL_EVENT_TYPES.includes(content.data?.notification?.category),
    );

    if (hasCriticalEvent) {
      this.banner.classList.add('fs__critical');
    } else {
      this.banner.classList.remove('fs__critical');
    }
  }

  public onClusterClick(): void {
    const clusterMarker = this.getMarker();
    clusterMarker.addListener('click', () => {
      if (this.cluster.bbox) {
        const lonLatBounds = SnLonLatBounds.fromGeojsonBBox(this.cluster.bbox);
        const bounds = SnLonLatBounds.convertToLatLng(lonLatBounds);
        this._map.panToBounds(lonLatBounds);
        this._map.setCenter(this.cluster.geometry.coordinates);
        this._map.mapInstance.fitBounds(bounds);
      }
    });
  }

  public toggleAnimation(on: boolean): void {
    this.animationOn = on ? true : false;
  }

  private _expandMarker(
    markerView: google.maps.marker.AdvancedMarkerElement,
    animationClass: string,
  ): void {
    const element = markerView.element;
    element.classList.add(animationClass);
    element.style.zIndex = '1';
  }

  private _collapseMarker(
    markerView: google.maps.marker.AdvancedMarkerElement,
    animationClass: string,
  ): void {
    const element = markerView.element;
    element.classList.remove(animationClass);
    element.style.zIndex = '';
  }

  private _initAnimation(
    markerView: google.maps.marker.AdvancedMarkerElement,
    animationClass: string,
  ): void {
    ['focus', 'pointerenter'].forEach((event) => {
      markerView.element.addEventListener(event, () => {
        if (!this.animationOn) {
          return;
        }
        this._expandMarker(markerView, animationClass);
      });
    });
    ['blur', 'pointerleave'].forEach((event) => {
      markerView.element.addEventListener(event, () => {
        if (!this.animationOn) {
          return;
        }
        this._collapseMarker(markerView, animationClass);
      });
    });
    markerView.element.addEventListener('click', () => {
      if (!this.animationOn) {
        return;
      }
      this._collapseMarker(markerView, animationClass);
    });
  }

  private _updatePosition(): void {
    if (!this.getLonLat()) {
      return;
    }
    if (this._popup) {
      this._popup.setLonLat(this.getLonLat());
    }
  }

  private _generateIcon(notification: string): string {
    return this._axonNotificationIconService?.getIconClass(notification) || 'fa-bell';
  }

  private _onBannerHover(banner: HTMLDivElement): void {
    const bannerConentElement = document.createElement('div');
    bannerConentElement.classList.add('fs__advanced-marker-icon-banner-expanded');
    bannerConentElement.innerHTML = this.bannerContent
      .map(
        (content) =>
          `
        <div class="fs_axon_marker_details">
          <div class="fs_axon_marker_icon">
            ${content.icon}
          </div>
          <div class="fs_axon_marker_text">
            <div class="fs_axon_marker_name_time">
              <span class="fs_axon_marker_name">${content.data.ownerName} - </span>
              <span class="fs_axon_marker_time">${new FsFormatDatePipe().transform(
                content.data.notification.notificationReceivedTime,
                'HH:mm',
              )}hrs</span>
            </div>
            <div class="fs_axon_marker_event">
              ${content.data.messageTranslation}
            </div>
          </div>
        </div>
        `,
      )
      .join(' ');

    banner.appendChild(bannerConentElement);

    bannerConentElement.onmouseleave = () => {
      bannerConentElement.remove();
    };
  }

  setVisible(visible: boolean): void {
    if (visible && !this.marker.map && this._map) {
      this.addTo(this._map);
    } else if (!visible && this.marker.map) {
      this.remove();
    }
  }

  getVisible(): boolean {
    return this.marker.map !== null;
  }
}
